import { api } from "@/lib/api-client";
import type { Paginated } from "../agents/api";

/** Trimmed template row from GET /template-library/ (list serializer). */
export type QuickstartTemplate = {
  id: string;
  name: string;
  suggested_name: string;
  description: string;
  category: string;
  icon: string;
  status: "draft" | "published";
  is_platform: boolean;
  sort_order: number;
};

/** Full template from GET /template-library/{id}/ (adds the content). */
export type QuickstartTemplateDetail = QuickstartTemplate & {
  greeting: string;
  system_prompt: string;
};

export const templatesApi = {
  quickstart: async (): Promise<QuickstartTemplate[]> => {
    const res = await api.get<Paginated<QuickstartTemplate> | QuickstartTemplate[]>(
      "/template-library/",
    );
    const rows = Array.isArray(res.data) ? res.data : res.data.results;
    return rows
      .filter((t) => t.status === "published")
      .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  },
  detail: async (id: string): Promise<QuickstartTemplateDetail> => {
    const res = await api.get<QuickstartTemplateDetail>(`/template-library/${id}/`);
    return res.data;
  },
};

/** One row of the admin-managed provider price catalog. */
export type ProviderKind = "transcriber" | "model" | "voice";

// Per-minute costs are intentionally absent: rates are the platform's
// internal cost card (Django admin only); tenants pay monthly plans.
export type ProviderOption = {
  id: string;
  kind: ProviderKind;
  provider: string;
  /** Display name for provider (backend get_provider_display). */
  provider_label: string;
  name: string;
  /** The model id calls send (agents store it as llm_model); catalog list only. */
  api_id?: string;
  latency_ms: number;
  is_default: boolean;
  sort_order: number;
  /** List price per 1M tokens in millicents; sent to tenant admins only. */
  input_price_millicents?: number | null;
  output_price_millicents?: number | null;
};

/** "$0.20 / $1.20 per 1M tokens", or "" when the caller gets no prices. */
export function tokenPriceText(o: ProviderOption, short = false): string {
  if (o.input_price_millicents == null || o.output_price_millicents == null) return "";
  const usd = (m: number) =>
    `$${(m / 100000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
  return `${usd(o.input_price_millicents)} / ${usd(o.output_price_millicents)} per 1M${short ? "" : " tokens"}`;
}

export const TOKEN_PRICE_HINT = "Prices are the provider's list price, billed to your own API key.";

/** One voice from the live provider listing (GET /assistants/voices/). */
export type ProviderVoice = {
  voice_id: string;
  name: string;
  gender: string;
  accent: string;
  language: string;
  preview_url: string;
  /** Set for ElevenLabs Voice-Library voices — needed to add them to the account. */
  public_owner_id: string;
  source: "mine" | "library";
};

export type VoiceFilters = {
  search?: string;
  language?: string;
  accent?: string;
};

export const voicesApi = {
  /** Live list from the provider's API using the tenant's own key — never
   * stored server-side beyond a short cache. For ElevenLabs this includes
   * the full public Voice Library, filtered server-side. */
  list: async (provider: string, filters: VoiceFilters = {}): Promise<ProviderVoice[]> => {
    const res = await api.get<{ voices: ProviderVoice[] }>("/assistants/voices/", {
      params: { provider, ...filters },
    });
    return res.data.voices;
  },

  /** Library voices must be added to the tenant's provider account before
   * calls can use them; returns the voice_id to store. */
  add: async (body: {
    provider: string;
    voice_id: string;
    public_owner_id: string;
    name: string;
  }): Promise<string> => {
    const res = await api.post<{ voice_id: string }>("/assistants/voices/add/", body);
    return res.data.voice_id;
  },
};

export type FirstMessageMode = "assistant_first" | "user_first" | "assistant_waits";

export const FIRST_MESSAGE_MODE_LABELS: Record<FirstMessageMode, string> = {
  assistant_first: "Assistant speaks first",
  user_first: "User speaks first",
  assistant_waits: "Assistant waits, then speaks",
};

export type Assistant = {
  id: string;
  name: string;
  first_message_mode: FirstMessageMode;
  first_message: string;
  system_prompt: string;
  transcriber: ProviderOption | null;
  llm: ProviderOption | null;
  voice: ProviderOption | null;
  transcriber_language: string;
  /** Provider voice the assistant speaks with, picked from the live list. */
  tts_voice_id: string;
  tts_voice_name: string;
  source_template: string | null;
  is_published: boolean;
  /** Advanced tab — how the call behaves, as opposed to what it says. */
  response_pace: ResponsePace;
  /** Draft the reply while Flux predicts the caller is finishing. */
  speculative_replies: boolean;
  /** Stop a reply at its first question and hold its tools until the answer. */
  wait_after_question: boolean;
  record_calls: boolean;
  /** 0 = no limit. */
  max_call_duration_minutes: number;
  /** 0 = never hang up on silence. */
  silence_timeout_seconds: number;
  /** Analysis tab: fields the AI extracts from every call transcript. */
  structured_outputs: StructuredOutputField[];
  /** Where callers reach this assistant. Read-only — a number's own
   * "Answered by" picker owns the mapping. */
  attached_numbers: AttachedNumber[];
  created_at: string;
  updated_at: string;
};

/** One number that answers with this assistant. The provider matters: it
 * decides which tools a call on it can run. */
export type ResponsePace = "snappy" | "balanced" | "relaxed";

export type AttachedNumber = {
  id: string;
  number: string;
  provider: string;
  channel: "voice" | "whatsapp";
};

export type StructuredOutputField = {
  key: string;
  label: string;
  type: "text" | "number" | "boolean";
  description: string;
};

export type AssistantPayload = Partial<{
  name: string;
  first_message_mode: FirstMessageMode;
  first_message: string;
  system_prompt: string;
  transcriber_language: string;
  tts_voice_id: string;
  tts_voice_name: string;
  is_published: boolean;
  response_pace: ResponsePace;
  speculative_replies: boolean;
  wait_after_question: boolean;
  record_calls: boolean;
  max_call_duration_minutes: number;
  silence_timeout_seconds: number;
  source_template: string | null;
  transcriber_id: string | null;
  llm_id: string | null;
  voice_id: string | null;
  structured_outputs: StructuredOutputField[];
}>;

export const assistantsApi = {
  list: async (): Promise<Assistant[]> => {
    const res = await api.get<Paginated<Assistant> | Assistant[]>("/assistants/");
    return Array.isArray(res.data) ? res.data : res.data.results;
  },
  create: async (payload: AssistantPayload): Promise<Assistant> => {
    const res = await api.post<Assistant>("/assistants/", payload);
    return res.data;
  },
  update: async (id: string, patch: AssistantPayload): Promise<Assistant> => {
    const res = await api.patch<Assistant>(`/assistants/${id}/`, patch);
    return res.data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/assistants/${id}/`);
  },
  duplicate: async (id: string): Promise<Assistant> => {
    const res = await api.post<Assistant>(`/assistants/${id}/duplicate/`);
    return res.data;
  },
  versions: async (id: string): Promise<AssistantVersion[]> => {
    const res = await api.get<AssistantVersion[]>(`/assistants/${id}/versions/`);
    return res.data;
  },
  restoreVersion: async (id: string, versionId: string): Promise<Assistant> => {
    const res = await api.post<Assistant>(
      `/assistants/${id}/versions/${versionId}/restore/`,
    );
    return res.data;
  },
  calls: async (id: string): Promise<AssistantCall[]> => {
    const res = await api.get<AssistantCall[]>(`/assistants/${id}/calls/`);
    return res.data;
  },
  analysis: async (id: string): Promise<AssistantAnalysis> => {
    const res = await api.get<AssistantAnalysis>(`/assistants/${id}/analysis/`);
    return res.data;
  },
};

/** Per-assistant call stats for the Analysis tab. */
export type AssistantAnalysis = {
  total_calls: number;
  completed: number;
  failed: number;
  in_progress: number;
  web_calls: number;
  phone_calls: number;
  inbound: number;
  outbound: number;
  total_seconds: number;
  avg_seconds: number;
  sentiment: {
    positive: number;
    neutral: number;
    negative: number;
    unanalyzed: number;
  };
  avg_lead_score: number | null;
  scored_calls: number;
  daily: { day: string; calls: number }[];
};

/** One row of an assistant's call log (Logs tab). Full detail lives on the
 * shared call detail page (/calls/{id}). */
export type AssistantCall = {
  id: string;
  type: "web" | "phone";
  direction: "inbound" | "outbound" | string;
  status: string;
  started_at: string | null;
  created_at: string;
  duration_seconds: number | null;
  sentiment: string | null;
};

/** One row of an assistant's version history (newest first = current). */
export type AssistantVersion = {
  id: string;
  short_id: string;
  action: "create" | "save" | "publish" | "restore";
  created_at: string;
  created_by: string;
  name: string;
  is_published: boolean;
  stack: { transcriber?: string; model?: string; voice?: string };
};

/** Session info to open a Talk WebSocket (POST /assistants/{id}/talk/). */
export type TalkSession = {
  call_id: string;
  ws_path: string;
  assistant_name: string;
};

export const talkApi = {
  initiate: async (assistantId: string): Promise<TalkSession> => {
    const res = await api.post<TalkSession>(`/assistants/${assistantId}/talk/`);
    return res.data;
  },
};

export const providerOptionsApi = {
  list: async (): Promise<ProviderOption[]> => {
    const res = await api.get<ProviderOption[]>("/assistants/provider-options/");
    return res.data;
  },
};

/* ── Tools ──────────────────────────────────────────────────────────────── */

/** What an assistant can do mid-call. Each kind runs on machinery the
 * platform already has — see apps/assistants/tools.py. */
export type ToolKind =
  | "api_request"
  | "transfer_call"
  | "end_call"
  | "send_text"
  | "calcom"
  | "odoo"
  | "press_digit";

/** Per-kind settings. Only the keys for the tool's own kind are used. */
export type ToolConfig = {
  // api_request
  url?: string;
  method?: string;
  headers?: Record<string, string>;
  signing_secret?: string;
  /** Read-only flag: a secret is saved, but never sent back. */
  has_signing_secret?: boolean;
  timeout_seconds?: number;
  // transfer_call
  destination?: string;
  // transfer_call / end_call: the line spoken before it happens
  message?: string;
  // send_text
  recipient?: "caller" | "fixed";
  number?: string;
  // press_digit: how long to let an automated prompt finish before pressing
  delay_ms?: number;
};

export type AssistantTool = {
  id: string;
  assistant: string;
  kind: ToolKind;
  kind_label: string;
  name: string;
  description: string;
  parameters_schema: Record<string, unknown>;
  config: ToolConfig;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type AssistantToolPayload = Partial<
  Pick<
    AssistantTool,
    "assistant" | "kind" | "name" | "description" | "parameters_schema" | "config" | "is_active"
  >
>;

/** One recorded run of an API Request tool. */
export type AssistantToolRun = {
  id: string;
  tool: string;
  tool_name: string;
  call: string | null;
  input_args: Record<string, unknown>;
  status: "ok" | "failed" | "ssrf_blocked" | "timeout";
  duration_ms: number;
  request_url: string;
  response_code: number | null;
  response_body: string;
  error_detail: string;
  created_at: string;
};

export const toolsApi = {
  list: async (assistantId: string): Promise<AssistantTool[]> => {
    const res = await api.get<AssistantTool[]>("/assistants/tools/", {
      params: { assistant: assistantId },
    });
    return res.data;
  },
  create: async (payload: AssistantToolPayload): Promise<AssistantTool> => {
    const res = await api.post<AssistantTool>("/assistants/tools/", payload);
    return res.data;
  },
  update: async (id: string, patch: AssistantToolPayload): Promise<AssistantTool> => {
    const res = await api.patch<AssistantTool>(`/assistants/tools/${id}/`, patch);
    return res.data;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/assistants/tools/${id}/`);
  },
  runs: async (id: string): Promise<AssistantToolRun[]> => {
    const res = await api.get<AssistantToolRun[]>(`/assistants/tools/${id}/runs/`);
    return res.data;
  },
};

/**
 * Instantiate template text for a new assistant: fill the variables we have
 * real values for. `{{agent_name}}` becomes the chosen assistant name and
 * `{{company_name}}`/`{{clinic_name}}`/`{{business_name}}` become the
 * tenant's company name. Runtime variables ({{chawowa_*}}) and anything
 * without a real source stay as {{...}} for the tenant to fill while editing.
 */
export function instantiateTemplate(
  text: string,
  assistantName: string,
  companyName: string,
): string {
  let out = text.replace(/\{\{\s*agent_name\s*\}\}/gi, assistantName);
  if (companyName) {
    out = out.replace(
      /\{\{\s*(?:company_name|clinic_name|business_name)\s*\}\}/gi,
      companyName,
    );
  }
  return out;
}
